export const REDIRECT_AFTER_ONBOARDING_KEY = 'redirectAfterOnboarding'

/** Плейсхолдер в localStorage; при онбординге подставляется реальный id проекта. */
export const REDIRECT_PROJECT_ID_PLACEHOLDER = '{projectId}'

type RouterLike = {
    resolve: (location: string) => { route: { matched: unknown[] } }
    push: (location: string) => Promise<unknown>
}

function readRawRedirectPath(): string | null {
    const raw = localStorage.getItem(REDIRECT_AFTER_ONBOARDING_KEY)
    if (!raw) return null

    try {
        const parsed = JSON.parse(raw)
        if (typeof parsed === 'string' && parsed.startsWith('/')) {
            return parsed
        }
        if (Array.isArray(parsed)) {
            const legacy = parsed.find((e) => e && typeof e === 'object' && 'path' in e) as { path?: string } | undefined
            if (legacy?.path) return String(legacy.path)
        }
        if (parsed && typeof parsed === 'object' && 'path' in parsed) {
            return String((parsed as { path: string }).path)
        }
    } catch {
        // plain string in storage
    }

    if (raw.startsWith('/')) return raw
    return null
}

export function getRedirectAfterOnboardingPath(): string | null {
    const stored = readRawRedirectPath()
    if (!stored) return null
    if (stored.includes(REDIRECT_PROJECT_ID_PLACEHOLDER)) {
        return stored
    }
    return toTemplatedRedirectPath(stored)
}

/**
 * Только на время сессии (до перезагрузки): после «Создать проект» с маркетплейса
 * не уводить на project/marketplace, пока не откроется онбординг.
 * localStorage при reload не читается — ключ redirectAfterOnboarding для tryRedirectAfterOnboarding.
 */
let suppressPublicMarketplaceRedirect = false

export function setSuppressPublicMarketplaceRedirect(value: boolean): void {
    suppressPublicMarketplaceRedirect = value
}

export function shouldSuppressPublicMarketplaceRedirect(): boolean {
    return suppressPublicMarketplaceRedirect
}

export function setRedirectAfterOnboardingPath(templatePath: string): void {
    localStorage.setItem(REDIRECT_AFTER_ONBOARDING_KEY, templatePath)
    setSuppressPublicMarketplaceRedirect(true)
}

export function clearRedirectAfterOnboarding(): void {
    localStorage.removeItem(REDIRECT_AFTER_ONBOARDING_KEY)
}

export function isPromoRedirectPath(path: string | null | undefined): boolean {
    return typeof path === 'string' && (path === '/promo' || path.startsWith('/promo/'))
}

/** Сброс только промо-возврата: не трогает маркетплейс и прочие redirectAfterOnboarding. */
export function clearPromoRedirectAfterOnboarding(): void {
    if (isPromoRedirectPath(getRedirectAfterOnboardingPath())) {
        clearRedirectAfterOnboarding()
    }
}

/** Любой путь маркетплейса → `/project/{projectId}/...` */
export function toTemplatedRedirectPath(sourcePath: string): string {
    if (sourcePath.includes(REDIRECT_PROJECT_ID_PLACEHOLDER)) {
        return sourcePath
    }

    const otherProjectMatch = sourcePath.match(/^\/project\/[^/]+(\/.*)?$/)
    if (otherProjectMatch) {
        return `/project/${REDIRECT_PROJECT_ID_PLACEHOLDER}${otherProjectMatch[1] || ''}`
    }

    if (sourcePath.startsWith('/marketplace')) {
        return `/project/${REDIRECT_PROJECT_ID_PLACEHOLDER}${sourcePath}`
    }

    return sourcePath
}

export function resolveRedirectAfterOnboardingPath(
    templatePath: string,
    projectId: string | number,
): string {
    return templatePath.split(REDIRECT_PROJECT_ID_PLACEHOLDER).join(String(projectId))
}

/** Публичный /marketplace/... → /project/{realId}/marketplace/... (после входа, не онбординг). */
export function toProjectScopedRedirectPath(sourcePath: string, projectId: string | number): string {
    const pid = String(projectId)
    const projectRoot = `/project/${pid}`

    if (sourcePath === projectRoot || sourcePath.startsWith(`${projectRoot}/`)) {
        return sourcePath
    }

    const otherProjectMatch = sourcePath.match(/^\/project\/[^/]+(\/.*)?$/)
    if (otherProjectMatch) {
        return `${projectRoot}${otherProjectMatch[1] || ''}`
    }

    if (sourcePath.startsWith('/marketplace')) {
        return `${projectRoot}${sourcePath}`
    }

    return sourcePath
}

export function isValidRedirectAfterOnboardingPath(path: string, router: RouterLike): boolean {
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.includes('://')) {
        return false
    }
    if (path.includes(REDIRECT_PROJECT_ID_PLACEHOLDER)) {
        return false
    }
    try {
        const { route } = router.resolve(path)
        return Array.isArray(route.matched) && route.matched.length > 0
    } catch {
        return false
    }
}

/** После онбординга: подставить id проекта и перейти по сохранённому шаблону. */
export async function tryRedirectAfterOnboarding(
    projectId: string | number | null | undefined,
    router: RouterLike,
): Promise<boolean> {
    if (projectId == null || projectId === '') return false

    const template = getRedirectAfterOnboardingPath()
    if (!template) return false

    const path = resolveRedirectAfterOnboardingPath(template, projectId)

    if (!isValidRedirectAfterOnboardingPath(path, router)) {
        clearRedirectAfterOnboarding()
        return false
    }

    clearRedirectAfterOnboarding()
    try {
        await router.push(path)
    } catch {
        // ignore: duplicate navigation
    }
    return true
}
